import { Vector2 } from "./Vector2";
import { FakeLodash } from "../coreutil/FakeLodash";
export var Rectangle;
(function (Rectangle) {
    function from(...points) {
        points = points.filter(p => p);
        if (points.length == 0)
            return null;
        return {
            topleft: { x: Math.min(...points.map(v => v.x)), y: Math.max(...points.map(v => v.y)) },
            botright: { x: Math.max(...points.map(v => v.x)), y: Math.min(...points.map(v => v.y)) },
        };
    }
    Rectangle.from = from;
    function fromOriginAndSize(origin, size) {
        return from(origin, Vector2.add(origin, size, { x: -1, y: -1 }));
    }
    Rectangle.fromOriginAndSize = fromOriginAndSize;
    function containsTile(box, tile) {
        return box.topleft.x - 0.5 <= tile.x
            && box.topleft.y + 0.5 >= tile.y
            && box.botright.x + 0.5 >= tile.x
            && box.botright.y - 0.5 <= tile.y;
    }
    Rectangle.containsTile = containsTile;
    function contains(box, tile) {
        return box.topleft.x <= tile.x
            && box.topleft.y >= tile.y
            && box.botright.x >= tile.x
            && box.botright.y <= tile.y;
    }
    Rectangle.contains = contains;
    function extend(box, padding) {
        if (Array.isArray(padding)) {
            const [padx, pady] = padding;
            return {
                topleft: Vector2.add(box.topleft, { x: -padx, y: pady }),
                botright: Vector2.add(box.botright, { x: padx, y: -pady }),
            };
        }
        else {
            return {
                topleft: Vector2.add(box.topleft, { x: -padding, y: padding }),
                botright: Vector2.add(box.botright, { x: padding, y: -padding }),
            };
        }
    }
    Rectangle.extend = extend;
    function shrink(rect, shrink) {
        return {
            topleft: Vector2.add(rect.topleft, { x: shrink.left ?? 0, y: shrink.top ?? 0 }),
            botright: Vector2.sub(rect.botright, { x: shrink.right ?? 0, y: shrink.bottom ?? 0 }),
        };
    }
    Rectangle.shrink = shrink;
    function clampInto(pos, area) {
        return {
            x: FakeLodash.clamp(pos.x, area.topleft.x, area.botright.x),
            y: FakeLodash.clamp(pos.y, area.botright.y, area.topleft.y),
        };
    }
    Rectangle.clampInto = clampInto;
    function center(box, snap = true) {
        let c = {
            x: (box.topleft.x + box.botright.x) / 2,
            y: (box.topleft.y + box.botright.y) / 2
        };
        if (snap)
            return Vector2.snap(c);
        else
            return c;
    }
    Rectangle.center = center;
    function left(rect) {
        return {
            topleft: { x: rect.topleft.x, y: rect.topleft.y },
            botright: { x: rect.topleft.x, y: rect.botright.y }
        };
    }
    Rectangle.left = left;
    function right(rect) {
        return {
            topleft: { x: rect.botright.x, y: rect.topleft.y },
            botright: { x: rect.botright.x, y: rect.botright.y }
        };
    }
    Rectangle.right = right;
    function top(rect) {
        return {
            topleft: { x: rect.topleft.x, y: rect.topleft.y },
            botright: { x: rect.botright.x, y: rect.topleft.y }
        };
    }
    Rectangle.top = top;
    function bottom(rect) {
        return {
            topleft: { x: rect.topleft.x, y: rect.botright.y },
            botright: { x: rect.botright.x, y: rect.botright.y }
        };
    }
    Rectangle.bottom = bottom;
    function topRight(rect) {
        return { x: rect.botright.x, y: rect.topleft.y };
    }
    Rectangle.topRight = topRight;
    function bottomLeft(rect) {
        return { x: rect.topleft.x, y: rect.botright.y };
    }
    Rectangle.bottomLeft = bottomLeft;
    function topLeft(rect) {
        return { x: rect.topleft.x, y: rect.topleft.y };
    }
    Rectangle.topLeft = topLeft;
    function bottomRight(rect) {
        return { x: rect.botright.x, y: rect.botright.y };
    }
    Rectangle.bottomRight = bottomRight;
    function tileWidth(rect) {
        return rect.botright.x - rect.topleft.x + 1;
    }
    Rectangle.tileWidth = tileWidth;
    function tileHeight(rect) {
        return rect.topleft.y - rect.botright.y + 1;
    }
    Rectangle.tileHeight = tileHeight;
    function width(rect) {
        return rect.botright.x - rect.topleft.x;
    }
    Rectangle.width = width;
    function height(rect) {
        return rect.topleft.y - rect.botright.y;
    }
    Rectangle.height = height;
    function extendTo(rect, ...tile) {
        return {
            topleft: {
                x: Math.min(rect.topleft.x, ...tile.map(t => t.x)),
                y: Math.max(rect.topleft.y, ...tile.map(t => t.y))
            },
            botright: {
                x: Math.max(rect.botright.x, ...tile.map(t => t.x)),
                y: Math.min(rect.botright.y, ...tile.map(t => t.y))
            }
        };
    }
    Rectangle.extendTo = extendTo;
    function extendToRect(rect, other) {
        return extendTo(extendTo(rect, other.topleft), other.botright);
    }
    Rectangle.extendToRect = extendToRect;
    function combine(...rects) {
        return Rectangle.from(...rects.flatMap(r => r ? [r.topleft, r.botright] : []));
    }
    Rectangle.combine = combine;
    function translate(rect, off) {
        return {
            topleft: Vector2.add(rect.topleft, off),
            botright: Vector2.add(rect.botright, off)
        };
    }
    Rectangle.translate = translate;
    function transform(rect, trans) {
        return Rectangle.from(Vector2.transform_point(rect.topleft, trans), Vector2.transform_point(rect.botright, trans));
    }
    Rectangle.transform = transform;
    function overlaps(a, b) {
        return (a.topleft.x <= b.botright.x && a.botright.x >= b.topleft.x) &&
            (a.botright.y <= b.topleft.y && a.topleft.y >= b.botright.y);
    }
    Rectangle.overlaps = overlaps;
    function centeredOn(center, width, height = undefined) {
        if (height == undefined)
            height = width;
        return {
            topleft: Vector2.add(center, { x: -width, y: height }),
            botright: Vector2.add(center, { x: width, y: -height }),
        };
    }
    Rectangle.centeredOn = centeredOn;
    function equals(a, b) {
        return (a == b) || (!!a && !!b && Vector2.eq(a.topleft, b.topleft) && Vector2.eq(a.botright, b.botright));
    }
    Rectangle.equals = equals;
    function containsRect(container, inner) {
        return Rectangle.containsTile(container, inner.topleft) && Rectangle.containsTile(container, inner.botright);
    }
    Rectangle.containsRect = containsRect;
    function screenOrigin(rect) {
        return Rectangle.bottomLeft(rect);
    }
    Rectangle.screenOrigin = screenOrigin;
    function union(...rects) {
        return Rectangle.from(...rects.flatMap(r => [r.topleft, r.botright]));
    }
    Rectangle.union = union;
    function size(rect) {
        return {
            x: rect.botright.x - rect.topleft.x + 1,
            y: rect.topleft.y - rect.botright.y + 1,
        };
    }
    Rectangle.size = size;
    function move(rect, offset) {
        return {
            topleft: Vector2.add(rect.topleft, offset),
            botright: Vector2.add(rect.botright, offset)
        };
    }
    Rectangle.move = move;
})(Rectangle || (Rectangle = {}));
